import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/services/api'
import type { AppConfig, ProjectType, DevelopmentLevel } from '@/types/api'

export const useConfigStore = defineStore('config', () => {
  // State
  const config = ref<AppConfig | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters - Backend-driven options
  const projectTypes = computed(() => config.value?.project_types || [])
  const developmentLevels = computed(() => config.value?.development_levels || [])
  const projectStatuses = computed(() => config.value?.project_statuses || [])
  
  const features = computed(() => config.value?.features || {})
  const validation = computed(() => config.value?.validation || {})
  const uiConfig = computed(() => config.value?.ui || {})

  // Computed helpers for display
  const getProjectTypeByValue = computed(() => (value: string) => 
    projectTypes.value.find(type => type.value === value)
  )
  
  const getDevelopmentLevelByValue = computed(() => (value: string) => 
    developmentLevels.value.find(level => level.value === value)
  )
  
  const getProjectStatusByValue = computed(() => (value: string) => 
    projectStatuses.value.find(status => status.value === value)
  )

  // Actions
  const loadConfig = async (): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.get('/config')
      
      if (response.data) {
        config.value = response.data
        return true
      } else {
        error.value = 'Failed to load application configuration'
        return false
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to load configuration'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const refreshConfig = async (): Promise<boolean> => {
    return loadConfig()
  }

  // Helper methods for smart rendering
  const formatEnumValue = (enumValue: any, fallback: string = 'Unknown'): string => {
    if (typeof enumValue === 'object' && enumValue?.label) {
      return enumValue.label
    }
    return fallback
  }

  const getEnumColor = (enumValue: any, fallback: string = 'gray'): string => {
    if (typeof enumValue === 'object' && enumValue?.color) {
      return enumValue.color
    }
    return fallback
  }

  const getEnumIcon = (enumValue: any, fallback: string = '📝'): string => {
    if (typeof enumValue === 'object' && enumValue?.icon) {
      return enumValue.icon
    }
    return fallback
  }

  const isFeatureEnabled = (featureKey: string): boolean => {
    return features.value[featureKey as keyof typeof features.value] || false
  }

  const getValidationRule = (ruleKey: string): any => {
    return validation.value[ruleKey as keyof typeof validation.value]
  }

  const getUiConfig = (configKey: string): any => {
    return uiConfig.value[configKey as keyof typeof uiConfig.value]
  }

  return {
    // State
    config, isLoading, error,
    // Getters
    projectTypes, developmentLevels, projectStatuses,
    features, validation, uiConfig,
    getProjectTypeByValue, getDevelopmentLevelByValue, getProjectStatusByValue,
    // Actions
    loadConfig, refreshConfig,
    // Helpers
    formatEnumValue, getEnumColor, getEnumIcon,
    isFeatureEnabled, getValidationRule, getUiConfig
  }
})